import config from 'common/config'
import Constants from 'common/constants'
import Http from 'common/http'
import {
  AgencyLogoPresignedUrlInterface,
  AgencyServiceInterface
} from './interfaces'
import Queries from './queries'

const basePath = `${config.API_URL}/${Constants.API_URl_PREFIX}/real-estate-agency`

const getToken = (): string => {
  try {
    const token = localStorage.getItem(Constants.TOKEN_LOCAL_STORAGE) || ''
    return token !== '' ? `Bearer ${token}` : ''
  } catch {
    return ''
  }
}

const AgencyService = {
  getDefaultAgency: (): Promise<AgencyServiceInterface> => {
    return Http.get<AgencyServiceInterface>(
      `${basePath}/default`,
      { credentials: 'omit' },
      true
    )
  },
  editDefaultAgency: (
    id: string,
    agency: Partial<AgencyServiceInterface>
  ): Promise<AgencyServiceInterface> => {
    return Http.put<Partial<AgencyServiceInterface>, AgencyServiceInterface>(
      `${basePath}/${id}`,
      agency
    )
  },
  uploadLogo: async (
    agencyId: string,
    file: File
  ): Promise<AgencyServiceInterface> => {
    const form = new FormData()
    form.append('file', file)
    const response = await fetch(`${basePath}/${agencyId}/logo`, {
      method: 'POST',
      headers: { Authorization: getToken() },
      body: form
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  getLogoPresignedUrl: async (
    agencyId: string,
    fileName: string
  ): Promise<AgencyLogoPresignedUrlInterface> => {
    const response = await fetch(
      `${basePath}/${agencyId}/logo/presigned-url?fileName=${encodeURIComponent(
        fileName
      )}`,
      {
        method: 'POST',
        headers: {
          Authorization: getToken(),
          'Content-Type': 'application/json'
        }
      }
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  uploadLogoToS3: async (
    presignedUrl: string,
    file: File,
    contentType: string
  ): Promise<void> => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
        'x-amz-acl': 'public-read'
      }
    })
    if (!response.ok)
      throw new Error(`S3 upload failed: ${response.statusText}`)
  },
  confirmLogoUpload: async (
    agencyId: string,
    logoUrl: string
  ): Promise<AgencyServiceInterface> => {
    const response = await fetch(`${basePath}/${agencyId}/logo/confirm`, {
      method: 'POST',
      headers: {
        Authorization: getToken(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ logoUrl })
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  getHeroImagePresignedUrl: async (
    agencyId: string,
    fileName: string
  ): Promise<AgencyLogoPresignedUrlInterface> => {
    const response = await fetch(
      `${basePath}/${agencyId}/hero-presigned-url?fileName=${encodeURIComponent(
        fileName
      )}`,
      {
        method: 'GET',
        headers: {
          Authorization: getToken()
        }
      }
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  markOnboardingCompleted: async (
    agencyId: string
  ): Promise<AgencyServiceInterface> => {
    const response = await fetch(
      `${basePath}/${agencyId}/onboarding-completed`,
      {
        method: 'PATCH',
        headers: { Authorization: getToken() }
      }
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  confirmHeroImageUpload: async (
    agencyId: string,
    heroImageUrl: string
  ): Promise<AgencyServiceInterface> => {
    const response = await fetch(`${basePath}/${agencyId}/hero-image`, {
      method: 'PUT',
      headers: {
        Authorization: getToken(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ heroImageUrl })
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  // Feature FAVICON: o PUT no S3 reusa `uploadLogoToS3` com o `contentType`
  // devolvido aqui
  getFaviconPresignedUrl: async (
    agencyId: string,
    fileName: string
  ): Promise<AgencyLogoPresignedUrlInterface> => {
    const response = await fetch(
      `${basePath}/${agencyId}/favicon/presigned-url?fileName=${encodeURIComponent(
        fileName
      )}`,
      {
        method: 'POST',
        headers: {
          Authorization: getToken(),
          'Content-Type': 'application/json'
        }
      }
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  confirmFaviconUpload: async (
    agencyId: string,
    faviconUrl: string
  ): Promise<AgencyServiceInterface> => {
    const response = await fetch(`${basePath}/${agencyId}/favicon`, {
      method: 'PUT',
      headers: {
        Authorization: getToken(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ faviconUrl })
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  removeFavicon: async (agencyId: string): Promise<AgencyServiceInterface> => {
    const response = await fetch(`${basePath}/${agencyId}/favicon`, {
      method: 'DELETE',
      headers: { Authorization: getToken() }
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw err
    }
    return response.json()
  },
  dismissOnboardingChecklist: (
    agencyId: string
  ): Promise<AgencyServiceInterface> =>
    Http.patch<undefined, AgencyServiceInterface>(
      `${basePath}/${agencyId}/dismiss-onboarding-checklist`
    )
}

export default AgencyService

export { Queries }
